<template>
  <div id="app">

    <component :is="component">
      <slot />
    </component>

    <ChatbotComponent v-if="isAuthChecked && $store.state.isAuthenticated" />
    <NotificationBadge v-if="isAuthChecked && $store.state.isAuthenticated" />
    <PaymentNotificationBadge v-if="isAuthChecked && $store.state.isAuthenticated" />
    <LoanReminderPopup v-if="isAuthChecked && $store.state.isAuthenticated" />

    <KYCPromptDialog
      v-if="isAuthChecked && $store.state.isAuthenticated && kycStatusLoaded && dashboardInfoLoaded"
      :balance="parsedBalance"
      :kyc-status="kycStatus"
      :user-id="userId"
    />

    <PaymentVerificationPopup />
    <BrowserTranslateToggle />

    <!-- ✅ Bannière de demande de permission -->
    <NativeNotificationPrompt
      v-model="showNativePrompt"
      :activation-fn="activationFn"
    />

  </div>
</template>

<script>
import axios from 'axios';
import VuebaseLayout from './layouts/VuebaseLayout';
import VuebaseLayout2 from './layouts/VuebaseLayout2';
import VuebaseLayout3 from './layouts/VuebaseLayout3';
import ChatbotComponent from './layouts/ChatbotComponent.vue';
import NotificationBadge from './layouts/NotificationBadge.vue';
import PaymentNotificationBadge from './layouts/PaymentNotificationBadge.vue';
import LoanReminderPopup from './layouts/LoanReminderPopup.vue';
import PaymentVerificationPopup from './layouts/CampaignVerificationPopup.vue';
import BrowserTranslateToggle from './components/BrowserTranslateToggle.vue';
import KYCPromptDialog from './components/KYCPromptDialog.vue';
import NativeNotificationPrompt from './components/NativeNotificationPrompt.vue';

export default {
  name: 'App',

  components: {
    VuebaseLayout,
    VuebaseLayout2,
    VuebaseLayout3,
    ChatbotComponent,
    NotificationBadge,
    PaymentNotificationBadge,
    LoanReminderPopup,
    PaymentVerificationPopup,
    BrowserTranslateToggle,
    KYCPromptDialog,
    NativeNotificationPrompt,
  },

  data() {
    return {
      shopping_bag: { items: [] },
      isAuthChecked: false,
      kycStatus: 'none',
      kycStatusLoaded: false,
      dashboard_info: {},
      dashboardInfoLoaded: false,
      pendingOpenChat: false,
      showNativePrompt: false,
      // ✅ Statut backend (source de vérité)
      backendSubscribed: false,
    };
  },

  async created() {
    const rawParams = new URLSearchParams(window.location.search);
    const fromRawUrl = rawParams.get('open_chat') === 'true';
    const fromRoute = this.$route?.query?.open_chat === 'true';
    this.pendingOpenChat = fromRawUrl || fromRoute;

    await this.$store.restored;

    const prevPaymentStatus = this.$store.state.paymentStatus;
    const prevAuthenticated = this.$store.state.isAuthenticated;

if (this.$store.state.isAuthenticated) {
      await this.$store.dispatch('checkPaymentStatus');
      this.checkKYCStatus();
      this.getDashboardInfo();
      await this.fetchSubscriptionStatus();
      this.checkCollectionNotifications();
      this.$store.dispatch('loadPartnerEligibility');
    }

    this.isAuthChecked = true;

    const statusChanged = prevPaymentStatus !== this.$store.state.paymentStatus
                          || prevAuthenticated !== this.$store.state.isAuthenticated;
    const onRoot = this.$route.path === '/';
    const routeForbidden = !this.isRouteAllowed(this.$route.path);

    if (statusChanged || onRoot || routeForbidden) {
      this.redirectBasedOnAuthStatus();
    }

    if (this.pendingOpenChat && this.$store.state.isAuthenticated) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$root.$emit('open-chatbot', {});
            this.pendingOpenChat = false;
          }, 600);
        });
      });
    }

    // L'affichage initial de la bannière est géré par le watcher
  },

  beforeCreate() {
    this.$store.commit('initialiseStore');
    const token = this.$store.state.token;
    if (token) {
      axios.defaults.headers.common['Authorization'] = "Token " + token;
    } else {
      axios.defaults.headers.common['Authorization'] = "";
    }
  },

  watch: {
    '$store.state.isAuthenticated': {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.checkKYCStatus();
          this.getDashboardInfo();
          await this.fetchSubscriptionStatus();
          this.updatePromptVisibility();
          this.$store.dispatch('loadPartnerEligibility');
        } else {
          this.kycStatus = 'none';
          this.kycStatusLoaded = false;
          this.dashboard_info = {};
          this.dashboardInfoLoaded = false;
          this.showNativePrompt = false;
        }
        if (this.isAuthChecked) {
          this.redirectBasedOnAuthStatus();
        }
      }
    },
    userId: {
      immediate: true,
      handler() {
        this.updatePromptVisibility();
      }
    },
    '$store.state.paymentStatus': {
      immediate: true,
      handler() {
        if (this.$store.state.isAuthenticated && this.isAuthChecked) {
          this.redirectBasedOnAuthStatus();
        }
      }
    }
  },

  computed: {
    component() {
      if (!this.isAuthChecked) return null;
      if (this.$route.meta.layout) {
        return this.resolveLayout(this.$route.meta.layout);
      }
      if (!this.$store.state.isAuthenticated) {
        return VuebaseLayout3;
      }
      if (this.$store.state.paymentStatus === true) {
        return VuebaseLayout2;
      }
      return VuebaseLayout;
    },

    parsedBalance() {
      if (!this.dashboard_info || !this.dashboard_info.data_six) return 0;
      const rawVal = String(this.dashboard_info.data_six);
      const cleanVal = rawVal.replace(/[^\d.]/g, '');
      const num = parseFloat(cleanVal);
      return isNaN(num) ? 0 : num;
    },

    userId() {
      return this.$store.getters.userId || 'anonymous';
    },
  },

  methods: {
    async checkCollectionNotifications() {
      if (!this.$store.state.isAuthenticated) return;
      try {
        await axios.get('/api/v1/manager/njangi/check-collection-notifications/');
      } catch (e) {
        // silent check
      }
    },

    // ── Appel API pour obtenir le statut de souscription ──────────
    async fetchSubscriptionStatus() {
      if (!this.$store.state.isAuthenticated) return;
      try {
        const response = await axios.get('/api/v1/webpush/status/', {
          params: { group: 'chat_users' },
          headers: { Authorization: `Token ${this.$store.state.token}` }
        });
        this.backendSubscribed = response.data.subscribed === true;
        console.log('[WebPush] Statut backend:', this.backendSubscribed);
      } catch (error) {
        console.warn('Erreur lors de la récupération du statut WebPush:', error);
        // En cas d'erreur, on utilise localStorage comme fallback
        this.backendSubscribed = false;
      }
    },

    // ── Mise à jour de la visibilité de la bannière ──────────────
    updatePromptVisibility() {
      const shouldShow = this.shouldShowPrompt();
      if (shouldShow && !this.showNativePrompt) {
        setTimeout(() => {
          this.showNativePrompt = true;
        }, 500);
      } else if (!shouldShow) {
        this.showNativePrompt = false;
      }
    },

    // ── Activation WebPush (appelée par la bannière) ──────────────
    async activationFn() {
      if (typeof window._initWebPush === 'function') {
        const success = await window._initWebPush();
        if (success) {
          const userId = this.$store.getters.userId;
          if (userId) {
            localStorage.setItem(`webpush_subscribed_${userId}`, 'true');
          }
          // ✅ Mettre à jour le statut backend localement
          this.backendSubscribed = true;
          this.showNativePrompt = false;
          // On peut aussi re-faire un appel pour être sûr (mais la souscription a déjà mis à jour le backend)
        }
        return success;
      } else {
        console.warn('_initWebPush non disponible');
        return false;
      }
    },

    // ── Décide si la bannière doit s'afficher ──────────────────────
    shouldShowPrompt() {
      if (typeof Notification === 'undefined') return false;
      if (Notification.permission === 'denied') return false;
      if (!this.isAuthChecked || !this.$store.state.isAuthenticated) return false;

      // 🔹 Priorité au statut backend
      if (this.backendSubscribed) return false;

      // 🔹 Fallback sur localStorage (pour les utilisateurs qui ont souscrit avant l'API)
      const userId = this.$store.getters.userId;
      if (userId && userId !== 'anonymous') {
        const flagKey = `webpush_subscribed_${userId}`;
        if (localStorage.getItem(flagKey) === 'true') return false;
      }

      return true;
    },

    // ── KYC ──────────────────────────────────────────────────────────
    async checkKYCStatus() {
      if (!this.$store.state.isAuthenticated) return;
      try {
        const res = await axios.get('/api/v1/home/kyc/status/');
        this.kycStatus = res.data.kyc_status || 'none';
        console.log('[KYC] kycStatus =', this.kycStatus);
      } catch (e) {
        console.error('[KYC] Erreur checkKYCStatus:', e);
        this.kycStatus = 'none';
      } finally {
        this.kycStatusLoaded = true;
      }
    },

    async getDashboardInfo() {
      if (!this.$store.state.isAuthenticated) return;
      try {
        const response = await axios.get('/api/v1/home/get/user/dashboard/Savings/');
        this.dashboard_info = response.data;
        console.log('[KYC] dashboard_info =', response.data);
        console.log('[KYC] parsedBalance =', this.parsedBalance);
      } catch (e) {
        console.error('[KYC] Erreur getDashboardInfo:', e);
        this.dashboard_info = {};
      } finally {
        this.dashboardInfoLoaded = true;
      }
    },

    // ── Layouts & routing ────────────────────────────────────────────
    resolveLayout(layoutName) {
      switch (layoutName) {
        case 'VuebaseLayout2': return VuebaseLayout2;
        case 'VuebaseLayout3': return VuebaseLayout3;
        default: return VuebaseLayout;
      }
    },

    isRouteAllowed(path) {
  const isAuth = this.$store.state.isAuthenticated;
  const hasPayment = this.$store.state.paymentStatus === true;

  if (!isAuth) {
    const pub = ['/', '/login', '/register', '/reset-password', '/market_place/items'];
    return pub.some(r => path === r || path.startsWith(r + '/'));
  }
  if (hasPayment) {
    const allowed = ['/', '/All', '/business-account/dashboard', '/logout', '/profile', '/market_place/items'];
    return allowed.some(r =>
      path === r || path.startsWith(r + '/') || path.startsWith('/business-account/')
    );
  }
  const allowed = ['/', '/All', '/logout', '/profile', '/business-account/payment', '/market_place/items'];
  return allowed.some(r => path === r || path.startsWith(r + '/'));
},

    handleBusinessSubscribe() {
      this.menuOpen = false;
      this.businessDialog = false;
      this.$router.push({ name: 'create_business_account4' });
    },

    redirectBasedOnAuthStatus() {
  if (!this.$router || !this.$route) return;

  const currentPath = this.$route.path;
  const isAuth = this.$store.state.isAuthenticated;
  const hasPayment = this.$store.state.paymentStatus === true;

  let targetHomePage = '/';
  if (isAuth && hasPayment) {
    targetHomePage = '/business-account/dashboard';
  } else if (isAuth && !hasPayment) {
    targetHomePage = '/All';
  }

  if (currentPath === '/' && isAuth) {
    if (targetHomePage !== '/' && currentPath !== targetHomePage) {
      this.$router.push(targetHomePage);
      return;
    }
  }

  if (isAuth) {
    if (hasPayment) {
      if (!['/', '/All', '/business-account/dashboard', '/logout', '/profile', '/market_place/items'].some(r =>
        currentPath === r ||
        currentPath.startsWith(r + '/') ||
        currentPath.startsWith('/business-account/')
      )) {
        if (currentPath !== '/business-account/dashboard') {
          this.$router.push('/business-account/dashboard');
        }
      }
    } else {
      if (!['/', '/All', '/logout', '/profile', '/business-account/payment', '/market_place/items'].some(r =>
        currentPath === r || currentPath.startsWith(r + '/')
      )) {
        if (currentPath !== '/All') {
          this.$router.push('/All');
        }
      }
    }
  } else {
    if (!['/', '/login', '/register', '/reset-password', '/market_place/items'].some(r =>
      currentPath === r || currentPath.startsWith(r + '/')
    )) {
      if (currentPath !== '/') {
        this.$router.push('/');
      }
    }
  }
}
  }
};
</script>

<style>
.v-toolbar__content {
  padding-left: 10px !important;
  padding-right: 0 !important;
}
.page_title > .v-toolbar__content > :first-child {
  width: 100% !important;
}
</style>